import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { NotificationItemType, NotificationType } from '../models/notifications-type.model';
import { TranslateService } from '@ngx-translate/core';
import { LottiePreloaderService } from './lottie-preloader.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  notifications: WritableSignal<NotificationType[]> = signal<NotificationType[]>([]);

  private nextId: number = 0;
  private lottiePreloaderService: LottiePreloaderService = inject(LottiePreloaderService);
  private activeTimeoutId: ReturnType<typeof setTimeout> | null = null;
  translate: TranslateService = inject(TranslateService);

  add(type: NotificationItemType, value?: string, duration: number = 5000): number {
    const id: number = ++this.nextId;
    const notification: NotificationType = this.constructNotification(type, duration, value);
    this.notifications.update((notifications: NotificationType[]) => [
      ...notifications,
      notification,
    ]);
    this.scheduleNext();
    return id;
  }

  private constructNotification(
    type: NotificationItemType,
    duration: number,
    value?: string,
  ): NotificationType {
    const informationsWithCustomTitle: NotificationItemType[] = [
      'upcomingIncline',
      'upcomingResistance',
    ];
    let title: string = '';
    if (type === 'upcomingIncline' && value) {
      value = `${Math.round(parseFloat(value) * 10) / 10}%`;
    }

    if (informationsWithCustomTitle.includes(type)) {
      title = value ? value : 'error';
    } else {
      title = this.translate.instant(`notification.${type}.title`);
    }

    const informationsWithCustomMsg: NotificationItemType[] = ['equipmentConnection'];
    const msg: string =
      informationsWithCustomMsg.includes(type) && value
        ? value
        : this.translate.instant(`notification.${type}.msg`);
    return {
      id: ++this.nextId,
      title: title,
      msg: msg,
      animationOptions: this.lottiePreloaderService.getOptions(type),
      type,
      duration,
      value: value || undefined,
    };
  }

  remove(id: number): void {
    this.notifications.update((notifications: NotificationType[]) =>
      notifications.filter((notification: NotificationType) => notification.id !== id),
    );
    this.clearActiveTimeout();
    if (!this.notifications()[0]) return;
    this.scheduleNext();
  }

  private scheduleNext(): void {
    if (this.activeTimeoutId !== null) return;
    if (!this.notifications()[0]) return;

    this.activeTimeoutId = setTimeout(() => {
      this.activeTimeoutId = null;
      this.remove(this.notifications()[0].id);
    }, this.notifications()[0].duration);
  }

  private clearActiveTimeout(): void {
    if (this.activeTimeoutId === null) return;
    clearTimeout(this.activeTimeoutId);
    this.activeTimeoutId = null;
  }
}
